import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import {ContentTemplate, Progress, TitleHeader} from 'web-library';
import {useTranslation} from 'react-i18next';
import RouteKey from '../../../routes/key';
import {
  usePostCreateNewGSCApplication
} from '../../../api-refactored/group-special-consideration/usePostCreateNewApplication';
import {
  useGetCheckExistingDraft
} from '../../../api-refactored/group-special-consideration/useGetCheckExistingDraft';
import InstructionsContent from '../Instructions/InstructionsContent';
import {AddCandidateMode} from '../CoordinatorListing/SpecialConsiderationCoordinatorListingTable';
import {ScInstructionInfo} from '../CoordinatorListing/SelectExamLevelCoordinatorModal';
import {
  useGetRequestSummary
} from '../../../api-refactored/group-special-consideration/useGetRequestSummary';
import {
  usePostContinueApplication
} from '../../../api-refactored/group-special-consideration/usePostContinueApplication';
import IndividualInstructionNotes from './IndividualInstructionNotes';
import GroupInstructionNotes from './GroupInstructionNotes';
import {GroupScType} from '../../../models/SpecialConsideration';
import {useWOGAA} from '../../../hooks/wogaa';

const {specialConsideration: specialConsiderationRoute} = RouteKey.child;
const {specialConsideration, coordinatorUploadRankingDetails} = RouteKey[
    'special-consideration'
    ].child;

const SpecialConsiderationCoordinatorInstructions = () => {
  const history = useHistory<ScInstructionInfo>();
  const examLevelUuid = history.location.state?.examLevelUuid;
  const examSeriesUuid = history.location.state?.examSeriesUuid;
  const addCandidateMode = history.location.state?.addCandidateMode;
  const applicationUuid = history.location.state?.applicationUuid;
  const isContinueApplication = history.location.state?.isContinueApplication;
  const [validationMsg, setValidationMsg] = useState<string>('');
  const [groupScType] = useState(
      addCandidateMode === AddCandidateMode.applyForIndividual ? 'IND' : 'GROUP'
  );

  const {t} = useTranslation();

  const {
    mutate: mutatePostCreateNewGSCApplication,
    status: newGSCApplicationStatus,
  } = usePostCreateNewGSCApplication({
    onSuccess: ({uuid}) => {
      if (uuid) {
        history.push(
            `/${specialConsiderationRoute}/${specialConsideration.route}/${uuid}/${coordinatorUploadRankingDetails.route}`
        );
      }
    },
    onError: ({response}) => {
      if (response) {
        setValidationMsg(response.data.message!);
      }
    },
  });

  const {data: checkExistingDraft} = useGetCheckExistingDraft({
    groupScType: groupScType || '',
    enabled: !!examLevelUuid && !isContinueApplication,
  });

  const {data: applicationData, isLoading: applicationDataLoading} = useGetRequestSummary({
    uuid: applicationUuid || '',
    enabled: !!applicationUuid && isContinueApplication,
  });

  const appUuid = isContinueApplication ? applicationUuid : checkExistingDraft?.uuid;

  const {mutate: postContinueApplication} = usePostContinueApplication({
    onSuccess: () =>
        history.push(
            `/${specialConsiderationRoute}/${specialConsideration.route}/${appUuid}/${coordinatorUploadRankingDetails.route}`
        ),
  });
 // Wogaa tracking start for SC application by coordinator //
  const {transactionStart} = useWOGAA('myexamsseab-6463');

  const startApplication = () => {
    if (examLevelUuid && examSeriesUuid) {
      transactionStart();
      mutatePostCreateNewGSCApplication({
        examLevelUuid,
        examSeriesUuid,
        groupScType,
      });
    }
  };

  const continueApplication = () => {
    if (appUuid) {
      transactionStart();
      postContinueApplication(appUuid);
    }
  };

  const applicationId = isContinueApplication
      ? applicationData?.applicationId
      : checkExistingDraft?.applicationId;

  const examLevel = isContinueApplication
      ? applicationData?.examLevel
      : checkExistingDraft?.examLevel;

  const isIndividualApplication = () => {
    if (addCandidateMode) {
      return addCandidateMode === AddCandidateMode.applyForIndividual;
    }
    return applicationData?.groupScType === GroupScType.IND;
  };

  // Should not allow user to access instruction page when they directly enter url in browser
  if (isContinueApplication === undefined) {
    history.push(`/${specialConsiderationRoute}/${specialConsideration.route}`);
    return <></>;
  }

  const isContinueApplicationBoolean =
      isContinueApplication || (checkExistingDraft ? checkExistingDraft.draft : false);

  const instructionNotes = isIndividualApplication() ? (
      <IndividualInstructionNotes/>
  ) : (
      <GroupInstructionNotes/>
  );

  return (
      <>
        <TitleHeader title={t('specialConsideration->>Instructions.ApplyForSpecialConsideration')}/>
        {applicationDataLoading ? (
            <ContentTemplate>
              <Progress color="secondary"/>
            </ContentTemplate>
        ) : (
            <InstructionsContent
                isContinueApplication={isContinueApplicationBoolean}
                applicationId={applicationId}
                examLevel={examLevel}
                continueApplication={continueApplication}
                startApplicationStatus={newGSCApplicationStatus}
                startApplication={startApplication}
                validationMsg={validationMsg}
                instructionNotes={instructionNotes}
            />
        )}
      </>
  );
};

export default SpecialConsiderationCoordinatorInstructions;
